import type { ReactNode } from "react";
import { useState } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { DownloadIcon } from "lucide-react";

import type { GlyphName } from "@/landing/lib/pixel-glyphs";
import { GLYPHS } from "@/landing/lib/pixel-glyphs";
import {
  AwardBadge,
  EmailJoinForm,
  JoinButton,
  PrimaryLink,
  TextLink,
  TrustLine,
} from "@/landing/system/actions";
import { Faq, JoinBand, PageHero } from "@/landing/system/blocks";
import { HeroCat, PixelCat } from "@/landing/system/cat";
import { CodeFrame } from "@/landing/system/code";
import {
  FilterPills,
  PillButton,
  PillLink,
  TextField,
} from "@/landing/system/controls";
import { BrowserFrame, TerminalFrame } from "@/landing/system/frames";
import { GlyphTiles, PixelGlyph } from "@/landing/system/glyphs";
import {
  Eyebrow,
  GlassCard,
  Section,
  SectionHeading,
} from "@/landing/system/layout";
import { GlassLink, Pill } from "@/landing/system/links";
import {
  AgentLogos,
  PLATFORM_NAMES,
  PlatformMark,
} from "@/landing/system/marks";
import {
  CapabilityGrid,
  CommandLine,
  LinkCard,
  PointList,
  Steps,
} from "@/landing/system/sections";
import { SideNavGroup, SideNavLink } from "@/landing/system/sidenav";

// Living reference for the Grid design system. Local dev only: every page in
// the redesign is built from these parts (docs/plans/website-redesign-scope.md).
export const Route = createFileRoute("/_landing/design")({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  head: () => ({
    meta: [
      { title: "Grid design system" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DesignPage,
});

const SWATCHES = [
  ["--g-card", "Card ground"],
  ["--g-lit", "Lit ground"],
  ["--g-shade", "Shade"],
  ["--g-ink", "Ink"],
  ["--g-ink-2", "Ink 2, body"],
  ["--g-ink-3", "Ink 3, muted"],
  ["--g-rule", "Rule"],
  ["--g-accent", "Accent, sparingly"],
] as const;

const GLYPH_NAMES = Object.keys(GLYPHS) as GlyphName[];

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-4 border-t border-(--g-rule-soft) py-8 md:grid-cols-[200px_minmax(0,1fr)]">
      <Eyebrow className="pt-1">{label}</Eyebrow>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function SideNavExample() {
  const [open, setOpen] = useState({ start: true, platforms: false });
  return (
    <nav aria-label="Example" className="max-w-[260px]">
      <SideNavGroup
        title="Getting started"
        open={open.start}
        onToggle={() => setOpen((prev) => ({ ...prev, start: !prev.start }))}
      >
        <SideNavLink to="/design" active>
          Overview
        </SideNavLink>
        <SideNavLink to="/design">Quick start</SideNavLink>
      </SideNavGroup>
      <SideNavGroup
        title="Platforms"
        open={open.platforms}
        onToggle={() =>
          setOpen((prev) => ({ ...prev, platforms: !prev.platforms }))
        }
      >
        <SideNavLink to="/design">LinkedIn</SideNavLink>
        <SideNavLink to="/design">Reddit</SideNavLink>
      </SideNavGroup>
    </nav>
  );
}

function DesignPage() {
  return (
    <>
      <PageHero
        eyebrow="Design system"
        title="Grid"
        sub="Every wonda.sh page is built from these parts. Missing one? Add it to src/landing/system and show it here."
        showAward
        visual={<HeroCat />}
      />

      <Section>
        <SectionHeading
          eyebrow="Reference"
          title="Tokens and parts"
          sub="Left-aligned, hairline rules, glass for surfaces, ink for the one primary action."
        />

        <div className="mt-12">
          <Row label="Colour">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {SWATCHES.map(([token, name]) => (
                <div key={token}>
                  <div
                    className="g-round-sm h-16 border border-(--g-rule-soft)"
                    style={{ background: `var(${token})` }}
                  />
                  <p className="mt-2 mb-0 text-[13px] text-(--g-ink)">{name}</p>
                  <p className="m-0 font-mono text-[11.5px] text-(--g-ink-3)">
                    {token}
                  </p>
                </div>
              ))}
            </div>
          </Row>

          <Row label="Type">
            <p className="m-0 text-[clamp(40px,4.8vw,70px)] leading-none font-medium tracking-[-0.035em]">
              Display, page h1
            </p>
            <p className="mt-6 mb-0 text-[clamp(30px,3.2vw,48px)] leading-[1.05] font-medium tracking-[-0.03em]">
              Section heading, h2
            </p>
            <p className="mt-5 mb-0 text-[21px] font-medium tracking-[-0.02em]">
              Card title, h3
            </p>
            <p className="mt-3 mb-0 max-w-[34em] text-[17px] leading-[1.5] text-(--g-ink-2)">
              Body. Your agent works in the background on LinkedIn, X and Reddit
              to find profiles, message, post <em>exactly</em> like you’re doing
              manually.
            </p>
            <p className="mt-3 mb-0 text-[13px] text-(--g-ink-3)">
              Small and muted, for notes and labels.
            </p>
            <Eyebrow className="mt-4">Mono label, eyebrows and badges</Eyebrow>
          </Row>

          <Row label="Actions">
            <div className="flex flex-wrap items-center gap-6">
              <JoinButton />
              <TextLink to="/docs">Read the docs</TextLink>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-6">
              <PrimaryLink href="#actions">
                <DownloadIcon aria-hidden className="size-4" />
                Update Wonda
              </PrimaryLink>
              <p className="m-0 text-[13px] text-(--g-ink-3)">
                PrimaryLink: the ink pill when the action isn’t Join.
              </p>
            </div>
            <EmailJoinForm className="mt-8" />
            <TrustLine className="mt-5" />
            <AwardBadge className="mt-6" />
          </Row>

          <Row label="Marks">
            <ul className="m-0 flex list-none flex-wrap gap-6 p-0 text-[15px]">
              {(
                Object.keys(PLATFORM_NAMES) as (keyof typeof PLATFORM_NAMES)[]
              ).map((platform) => (
                <li key={platform} className="inline-flex items-center gap-2">
                  <PlatformMark platform={platform} />
                  {PLATFORM_NAMES[platform]}
                </li>
              ))}
            </ul>
            <AgentLogos className="mt-6" />
          </Row>

          <Row label="Cards">
            <div className="grid gap-4 md:grid-cols-3">
              {(["linkedin", "sales-navigator", "x"] as const).map(
                (platform) => (
                  <GlassCard key={platform}>
                    <PlatformMark platform={platform} className="size-5" />
                    <p className="mt-5 mb-2 text-[19px] font-medium tracking-[-0.02em]">
                      {PLATFORM_NAMES[platform]}
                    </p>
                    <p className="m-0 text-[14px] leading-[1.55] text-(--g-ink-2)">
                      One short paragraph about what the agent does here, in
                      plain words.
                    </p>
                  </GlassCard>
                ),
              )}
            </div>
          </Row>

          <Row label="Frames">
            <div className="grid gap-4 lg:grid-cols-2">
              <TerminalFrame
                title="codex"
                lines={[
                  { kind: "ask", text: "Reply to my 4 new X mentions." },
                  { kind: "run", text: "wonda x mentions -n 4" },
                  { kind: "out", text: "  4 mentions since yesterday" },
                ]}
              />
              <BrowserFrame
                url="linkedin.com/sales/lead/…"
                badge="In background"
              >
                <p className="m-0 text-[14px] text-(--g-ink-2)">
                  What Wonda’s browser is doing, drawn with system parts.
                </p>
              </BrowserFrame>
            </div>
          </Row>

          <Row label="Command line">
            <div className="grid max-w-[34rem] gap-3">
              <CommandLine>wonda x mentions -n 20</CommandLine>
              <CommandLine kind="ask">Reply to my new X mentions.</CommandLine>
              <CommandLine kind="plain">
                https://api.wondercat.ai/mcp
              </CommandLine>
            </div>
          </Row>

          <Row label="Capabilities">
            <CapabilityGrid
              items={[
                {
                  title: "Find the right people",
                  body: "Search people and companies, then read full profiles.",
                  command:
                    'wonda linkedin search "head of growth" --type PEOPLE',
                },
                {
                  title: "Post with media",
                  body: "Up to four images, GIFs or videos, typed into the real composer.",
                  command:
                    'wonda x tweet "We just shipped" --attach ./demo.mp4',
                },
                {
                  title: "Know the room",
                  body: "Read a subreddit's rules before saying anything.",
                  command: "wonda reddit rules SaaS",
                },
              ]}
            />
          </Row>

          <Row label="Steps">
            <Steps
              items={[
                {
                  title: "Install Wonda",
                  body: "Join, then run the install command from your account.",
                  line: {
                    kind: "run",
                    text: "curl -fsSL https://wonda.sh/install.sh | bash -s -- <your code>",
                  },
                },
                {
                  title: "Sign in",
                  body: "Sign in once in the Wonda browser on your computer.",
                  line: [
                    { kind: "run", text: "wonda wab login me linkedin" },
                    { kind: "run", text: "wonda wab login me x" },
                  ],
                },
                {
                  title: "Ask your agent",
                  body: "It works in the background while you do something else.",
                  line: {
                    kind: "ask",
                    text: "Connect with this week's commenters.",
                  },
                },
              ]}
            />
          </Row>

          <Row label="Points">
            <PointList
              items={[
                {
                  title: "Your browser, your session",
                  body: "A real browser on your computer, signed in as you.",
                },
                {
                  title: "Human pacing",
                  body: "Reads are spaced out and writes count against daily limits.",
                },
              ]}
            />
          </Row>

          <Row label="Glyph tiles">
            <GlyphTiles
              items={[
                {
                  glyph: "search",
                  platform: "linkedin",
                  title: "Find the right people",
                  body: "Search people and companies, then read full profiles.",
                },
                {
                  glyph: "connect",
                  platform: "linkedin",
                  title: "Connect with a note",
                  body: "Send invites with a personal note.",
                },
                {
                  glyph: "reply",
                  platform: "reddit",
                  eyebrow: (
                    <>
                      <PlatformMark platform="reddit" />
                      {PLATFORM_NAMES.reddit}
                    </>
                  ),
                  title: "“Answer the new comments on my post.”",
                },
              ]}
            />
          </Row>

          <Row label="Glyphs">
            <ul className="m-0 grid list-none grid-cols-3 gap-4 p-0 sm:grid-cols-5 lg:grid-cols-10">
              {GLYPH_NAMES.map((glyph) => (
                <li key={glyph} className="flex flex-col items-center gap-2">
                  <PixelGlyph glyph={glyph} platform="linkedin" />
                  <Eyebrow>{glyph}</Eyebrow>
                </li>
              ))}
            </ul>
          </Row>

          <Row label="Link cards">
            <div className="grid gap-4 md:grid-cols-3">
              {(["linkedin", "x", "reddit"] as const).map((platform) => (
                <LinkCard
                  key={platform}
                  to="/platform/$slug"
                  params={{ slug: platform }}
                  icon={<PlatformMark platform={platform} className="size-5" />}
                  title={PLATFORM_NAMES[platform]}
                  body="One line about the page it links to."
                />
              ))}
            </div>
          </Row>

          <Row label="Prose">
            <div className="g-prose max-w-[46rem]">
              <h3>Long-form text</h3>
              <p>
                Docs, legal and blog posts render inside <code>.g-prose</code>.
                Links are <a href="#prose">underlined ink</a>, never coloured.
              </p>
              <ul>
                <li>Lists keep a muted marker.</li>
                <li>
                  Inline code like <code>wonda x mentions -n 4</code> sits in a
                  soft chip.
                </li>
              </ul>
              <div className="g-table-scroll g-table-stack">
                <table>
                  <thead>
                    <tr>
                      <th>Command</th>
                      <th>What it does</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td data-label="Command">
                        <code>wonda x mentions</code>
                      </td>
                      <td data-label="What it does">
                        Reads your latest X mentions.
                      </td>
                    </tr>
                    <tr>
                      <td data-label="Command">
                        <code>wonda reddit feed</code>
                      </td>
                      <td data-label="What it does">Reads a subreddit feed.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <blockquote className="g-callout">
                <p>
                  <strong>Callout.</strong> A glass note for TL;DRs, key
                  takeaways and warnings.
                </p>
              </blockquote>
              <blockquote>
                <p>A plain blockquote keeps the hairline on its left.</p>
              </blockquote>
            </div>
          </Row>

          <Row label="Code">
            <CodeFrame
              language="bash"
              code={`wonda linkedin search "VP marketing Berlin" --type PEOPLE\nwonda linkedin profile johndoe`}
            />
            <CodeFrame className="mt-4" code="wonda x mentions -n 4" />
          </Row>

          <Row label="Link cards and pills">
            <div className="grid gap-4 md:grid-cols-3">
              <GlassLink to="/blog">
                <Eyebrow>Tutorials · 6 min read</Eyebrow>
                <p className="mt-4 mb-2 text-[19px] font-medium tracking-[-0.02em]">
                  A card that is one link
                </p>
                <p className="m-0 text-[14px] leading-[1.55] text-(--g-ink-2)">
                  Lifts on hover. Used for blog posts and page lists.
                </p>
              </GlassLink>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Pill to="/design" active>
                All
              </Pill>
              <Pill to="/design">Tutorials</Pill>
              <Pill to="/design">Guides</Pill>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Pill to="/design" size="sm">
                #linkedin
              </Pill>
              <Pill to="/design" size="sm" active>
                #reddit
              </Pill>
            </div>
          </Row>

          <Row label="Page header">
            <SectionHeading
              as="h1"
              size="page"
              eyebrow="Getting started"
              title="Quick start"
              sub="Docs and legal pages: the h1 at the page scale, the group as its eyebrow."
            />
          </Row>

          <Row label="Side nav">
            <SideNavExample />
          </Row>

          <Row label="FAQ">
            <Faq
              items={[
                {
                  question: "Does it run on my machine?",
                  answer:
                    "Yes. Wonda is a real browser on your computer, signed in as you.",
                },
                {
                  question: "Which agents does it work with?",
                  answer:
                    "Claude Code, Codex, Claude, ChatGPT, OpenClaw and Hermes.",
                },
              ]}
            />
          </Row>

          <ControlRows />
        </div>
      </Section>

      <JoinBand />
    </>
  );
}

// Pills, fields and the cat (added with /pricing, /models, /affiliates and the 404).
function ControlRows() {
  const [filter, setFilter] = useState<"all" | "image" | "video">("all");
  return (
    <>
      <Row label="Pills">
        <div className="flex flex-wrap items-center gap-3">
          <PillLink to="/">Glass pill, secondary</PillLink>
          <PillButton>Ink pill, not sign-up</PillButton>
          <PillButton disabled>Disabled</PillButton>
        </div>
      </Row>

      <Row label="Fields">
        <div className="grid max-w-[440px] gap-5">
          <TextField
            label="Name"
            name="design-name"
            placeholder="Ada Lovelace"
          />
          <FilterPills
            label="Category"
            value={filter}
            onChange={setFilter}
            options={[
              { value: "all", label: "All" },
              { value: "image", label: "Image" },
              { value: "video", label: "Video" },
            ]}
          />
        </div>
      </Row>

      <Row label="Cat">
        <PixelCat className="w-40" title="Wonda" />
      </Row>
    </>
  );
}
